import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Api, type PublicationLogEntry, type DayVideoInfo } from "../api/client";
import { format, addDays, parseISO } from "date-fns";
import { RefreshCw, ExternalLink, Film, Image as ImageIcon } from "lucide-react";

const SIGN_NAMES: Record<string, string> = {
  aries: "Овен", taurus: "Телец", gemini: "Близнецы", cancer: "Рак",
  leo: "Лев", virgo: "Дева", libra: "Весы", scorpio: "Скорпион",
  sagittarius: "Стрелец", capricorn: "Козерог", aquarius: "Водолей", pisces: "Рыбы",
};

const CHANNEL_NAMES: Record<string, string> = {
  telegram: "Telegram",
  vk: "VKontakte",
  ok: "Одноклассники",
  max: "MAX",
  // Ролик дня — отдельная строка лога: он уезжает в видеораздел группы, а не в ленту.
  vk_video: "VK · видеораздел",
  ok_video: "ОК · видеораздел",
  // История — вертикаль со ссылкой в приложение, живёт 24 часа.
  vk_story: "VK · история",
};

/** Каналы с видеоразделом — туда и только туда уезжает ролик дня. */
const VIDEO_CHANNELS: Array<[string, string]> = [
  ["vk_video", "VKontakte"],
  ["ok_video", "Одноклассники"],
];

/**
 * Куда уезжает ИСТОРИЯ. Пока только ВК: у ОК моменты принимают исключительно фото,
 * методов stories.* там нет вовсе (проверено на живом API 11.08.2026).
 */
const STORY_CHANNELS: Array<[string, string]> = [["vk_story", "VKontakte"]];

function statusBadge(status: string | undefined | null) {
  if (!status) return <span className="badge-pending">—</span>;
  const lc = status.toLowerCase();
  // ВАЖНО: order matters — "publishing" должен матчиться раньше чем "publish"-prefix,
  // иначе двухфазный коммит «висящие» строки будут показаны как «опубликовано».
  if (lc === "publishing")
    return <span className="badge-pending" title="phase-1 saved, post not yet finalized">в процессе…</span>;
  if (lc === "published" || lc === "ok")
    return <span className="badge-ok">опубликовано</span>;
  if (lc.includes("fail") || lc === "failed" || lc === "error")
    return <span className="badge-fail">{status}</span>;
  return <span className="badge-pending">{status}</span>;
}

/**
 * Что реально уехало в канал. Источник — media_kind в payload_json, который пишет
 * PublishOrchestrator в момент публикации.
 *
 * "unknown" показываем честно, а не как «картинку»: у записей, созданных до появления
 * маркера, признака нет вовсе, и молча выдавать их за картинки — врать в отчётности.
 */
function mediaBadge(s: PublicationLogEntry) {
  const kind = (s.mediaKind ?? "unknown").toLowerCase();

  if (kind === "video") {
    const dur = s.videoDurationSec ? `${s.videoDurationSec} с` : null;
    const dims = s.videoWidth && s.videoHeight ? `${s.videoWidth}×${s.videoHeight}` : null;
    const vertical = !!(s.videoWidth && s.videoHeight && s.videoHeight > s.videoWidth);
    return (
      <span
        className="badge-ok inline-flex items-center gap-1"
        title={[dims, dur, s.videoKind].filter(Boolean).join(" · ")}
      >
        <Film size={11} />
        {vertical ? "вертикаль" : "горизонт"}
        {dur && <span className="text-white/60">· {dur}</span>}
      </span>
    );
  }
  if (kind === "image")
    return (
      <span className="badge-pending inline-flex items-center gap-1">
        <ImageIcon size={11} />
        картинка
      </span>
    );
  return <span className="badge-pending" title="запись создана до появления media-маркера">—</span>;
}

const isVertical = (v: DayVideoInfo) => !!(v.widthPx && v.heightPx && v.heightPx > v.widthPx);
const mb = (bytes: number) => `${(bytes / 1048576).toFixed(1)} МБ`;

export function Publications() {
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ["publications", selectedDate],
    queryFn: () => Api.publications(selectedDate),
    retry: 1,
  });

  const mRetryFailed = useMutation({
    mutationFn: () => Api.retryFailed(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["publications", selectedDate] }),
  });

  const mPublishOne = useMutation({
    mutationFn: ({ sign }: { sign: string }) =>
      Api.triggerPublish(selectedDate, undefined, sign),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["publications", selectedDate] }),
  });
  // Combined-режим: публикуем один объединённый пост дня (sign_code='all').
  const mPublishCombined = useMutation({
    mutationFn: () => Api.triggerPublish(selectedDate, "dzen", "all"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["publications", selectedDate] }),
  });

  const shift = (days: number) =>
    setSelectedDate(format(addDays(parseISO(selectedDate), days), "yyyy-MM-dd"));

  const social = status.data?.social ?? [];
  const dayVideo = status.data?.dayVideo ?? null;
  const dzen = status.data?.dzen ?? [];
  // Канонический Дзен-вывод — объединённый пост дня; пер-знак статьи теперь легаси.
  const combinedDzen = dzen.find(d => d.signCode === "all");
  const perSignDzen = dzen.filter(d => d.signCode !== "all");
  const dzenFailed = dzen.filter(d => d.status === "failed" && d.attemptCount < 5);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">Публикации</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="btn-secondary">←</button>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          <button onClick={() => shift(1)} className="btn-secondary">→</button>
          <button onClick={() => setSelectedDate(format(new Date(), "yyyy-MM-dd"))}
            className="btn-secondary">Сегодня</button>
          {dzenFailed.length > 0 && (
            <button
              onClick={() => mRetryFailed.mutate()}
              disabled={mRetryFailed.isPending}
              className="btn-secondary text-amber-300 border-amber-500/30 hover:bg-amber-500/10">
              <RefreshCw size={14} className={mRetryFailed.isPending ? "animate-spin inline mr-1" : "inline mr-1"} />
              Retry failed ({dzenFailed.length})
            </button>
          )}
        </div>
      </div>

      {status.isLoading && <p className="text-white/40 text-sm">Загружаем…</p>}
      {status.error && (
        <div className="card p-4 bg-rose-500/10 border-rose-500/30 text-rose-200 text-sm">
          Ошибка загрузки: {String((status.error as Error).message)}
        </div>
      )}

      {/* Ролик дня */}
      {dayVideo && (
        <section className="card p-6">
          <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
            <Film size={18} /> Ролик дня
          </h2>
          <div className="flex flex-col md:flex-row gap-5">
            <video
              src={`/api/media/${dayVideo.assetId}/file`}
              controls
              preload="metadata"
              className={
                "rounded-lg bg-black/40 border border-white/10 " +
                (isVertical(dayVideo) ? "h-64 w-auto" : "w-96 h-auto")
              }
            />
            <div className="text-sm space-y-1.5">
              <div className="flex flex-wrap gap-2">
                <span className="badge-ok">
                  {isVertical(dayVideo) ? "вертикаль 9:16" : "горизонт 16:9"}
                </span>
                {dayVideo.widthPx && dayVideo.heightPx && (
                  <span className="badge-pending font-mono">
                    {dayVideo.widthPx}×{dayVideo.heightPx}
                  </span>
                )}
                {dayVideo.durationSec ? (
                  <span className="badge-pending">{dayVideo.durationSec} с</span>
                ) : null}
                {dayVideo.fileSizeBytes ? (
                  <span className="badge-pending">{mb(dayVideo.fileSizeBytes)}</span>
                ) : null}
              </div>
              <p className="text-white/40 text-xs font-mono break-all">{dayVideo.remotePath ?? "—"}</p>
              <p className="text-white/40 text-xs">
                сгенерён {new Date(dayVideo.createdAt).toLocaleString("ru-RU")}
              </p>
              {/* Куда уехал, а куда нет — главный вопрос этой страницы.
                  Смотрим строки видеораздела: пост дня остаётся картинкой всегда. */}
              <div className="pt-2 flex flex-wrap gap-2">
                {VIDEO_CHANNELS.map(([code, name]) => {
                  const row = social.find((s) => s.channelCode?.toLowerCase() === code);
                  const published = (row?.status ?? "").toLowerCase() === "published";
                  return (
                    <span
                      key={code}
                      className={published ? "badge-ok" : "badge-pending"}
                      title={
                        !row ? "заливки в видеораздел ещё не было"
                          : published ? `в видеоразделе${row.externalUrl ? ` · ${row.externalUrl}` : ""}`
                          : `${row.status}${row.errorMessage ? ` · ${row.errorMessage}` : ""}`
                      }
                    >
                      {name}: {!row ? "—" : published ? "в видеоразделе" : row.status}
                    </span>
                  );
                })}
              </div>
              {/* История — отдельная поверхность: вертикаль, 24 часа, ссылка в приложение */}
              <div className="pt-2 flex flex-wrap gap-2 items-center">
                <span className="text-white/40 text-xs">История со ссылкой в приложение:</span>
                {STORY_CHANNELS.map(([code, name]) => {
                  const row = social.find((s) => s.channelCode?.toLowerCase() === code);
                  const published = (row?.status ?? "").toLowerCase() === "published";
                  return (
                    <span
                      key={code}
                      className={published ? "badge-ok" : "badge-pending"}
                      title={
                        !row ? "истории на эту дату не было"
                          : published ? `${row.externalUrl ?? ""} · живёт 24 часа`
                          : `${row.status}${row.errorMessage ? ` · ${row.errorMessage}` : ""}`
                      }
                    >
                      {name}: {!row ? "—" : published ? "опубликована" : row.status}
                    </span>
                  );
                })}
              </div>
              <p className="text-white/30 text-xs pt-1">
                Ролик кладётся в видеораздел группы и в ленте не появляется:
                пост дня — по-прежнему одна картинка с текстом. История берёт вертикаль
                и несёт кликабельную ссылку в Лунару — это единственный переход в одно касание.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Социалки */}
      <section className="card p-6">
        <h2 className="font-display text-xl font-semibold mb-4">Социальные каналы</h2>
        {!status.isLoading && social.length === 0 && (
          <p className="text-white/40 text-sm">Пока ничего не опубликовано на эту дату.</p>
        )}
        {social.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-white/40 text-xs uppercase">
                <tr>
                  <th className="text-left py-2 pr-4">Канал</th>
                  <th className="text-left py-2 pr-4">Медиа</th>
                  <th className="text-left py-2 pr-4">Статус</th>
                  <th className="text-left py-2 pr-4">Post</th>
                  <th className="text-right py-2">Опубликовано</th>
                </tr>
              </thead>
              <tbody>
                {social.map((s) => (
                  <tr key={s.id} className="border-t border-white/5">
                    <td className="py-2 pr-4 font-medium">
                      {CHANNEL_NAMES[s.channelCode?.toLowerCase()] ?? s.channelCode ?? "—"}
                    </td>
                    <td className="py-2 pr-4">{mediaBadge(s)}</td>
                    <td className="py-2 pr-4">{statusBadge(s.status)}</td>
                    <td className="py-2 pr-4">
                      {s.externalUrl ? (
                        <a href={s.externalUrl} target="_blank" rel="noreferrer"
                           className="text-astro-violet hover:underline inline-flex items-center gap-1 text-xs">
                          <span className="font-mono">{s.externalPostId ?? "open"}</span>
                          <ExternalLink size={11} />
                        </a>
                      ) : s.externalPostId ? (
                        <span className="font-mono text-xs text-white/70">{s.externalPostId}</span>
                      ) : "—"}
                    </td>
                    <td className="py-2 text-right text-white/40 text-xs">
                      {s.publishedAt ? new Date(s.publishedAt).toLocaleString("ru-RU") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Дзен — пост дня (combined-режим: 1 объединённый пост) */}
      <section className="card p-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="font-display text-xl font-semibold">Дзен — пост дня</h2>
          <button
            onClick={() => mPublishCombined.mutate()}
            disabled={mPublishCombined.isPending}
            className="btn-secondary text-sm disabled:opacity-40">
            {mPublishCombined.isPending
              ? <><RefreshCw size={14} className="animate-spin inline mr-1" />Публикуем…</>
              : combinedDzen?.status === "published" ? "Опубликовать заново" : "Опубликовать пост дня"}
          </button>
        </div>
        {!status.isLoading && !combinedDzen && (
          <p className="text-white/40 text-sm">Объединённый пост дня на эту дату ещё не публиковался.</p>
        )}
        {combinedDzen && (
          <div className="bg-black/20 rounded-lg px-4 py-3 border border-astro-violet/20">
            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
              <span className="font-medium">Пост дня (все знаки)</span>
              <div className="flex items-center gap-2">
                {statusBadge(combinedDzen.status)}
                {combinedDzen.status === "failed" && combinedDzen.attemptCount < 5 && (
                  <button
                    onClick={() => mPublishCombined.mutate()}
                    disabled={mPublishCombined.isPending}
                    className="text-xs px-2 py-0.5 rounded border border-amber-500/30 text-amber-300 hover:bg-amber-500/10">
                    retry
                  </button>
                )}
              </div>
            </div>
            {combinedDzen.postUrl && (
              <a href={combinedDzen.postUrl} target="_blank" rel="noreferrer"
                 className="text-xs text-astro-violet hover:underline truncate flex items-center gap-1">
                <ExternalLink size={11} />
                <span className="truncate">{combinedDzen.postUrl}</span>
              </a>
            )}
            {combinedDzen.lastErrorKind && (
              <p className="text-xs text-rose-300/70 mt-1">
                {combinedDzen.lastErrorKind} (attempts: {combinedDzen.attemptCount})
              </p>
            )}
            {combinedDzen.lastError && (
              <p className="text-xs text-rose-300/50 mt-1 line-clamp-2">{combinedDzen.lastError}</p>
            )}
          </div>
        )}

        {/* Пер-знак статьи — легаси-режим, свёрнуты чтобы не мешали */}
        {perSignDzen.length > 0 && (
          <details className="mt-4">
            <summary className="text-xs text-white/40 cursor-pointer hover:text-white/60 select-none">
              Пер-знак статьи (легаси-режим) · {perSignDzen.length}
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
              {perSignDzen.map((d) => (
                <div key={d.id} className="bg-black/20 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <span className="font-medium">{SIGN_NAMES[d.signCode] ?? d.signCode}</span>
                    <div className="flex items-center gap-2">
                      {statusBadge(d.status)}
                      {d.status === "failed" && d.attemptCount < 5 && (
                        <button
                          onClick={() => mPublishOne.mutate({ sign: d.signCode })}
                          disabled={mPublishOne.isPending}
                          className="text-xs px-2 py-0.5 rounded border border-amber-500/30 text-amber-300 hover:bg-amber-500/10">
                          retry
                        </button>
                      )}
                    </div>
                  </div>
                  {d.postUrl && (
                    <a href={d.postUrl} target="_blank" rel="noreferrer"
                       className="text-xs text-astro-violet hover:underline truncate flex items-center gap-1">
                      <ExternalLink size={11} />
                      <span className="truncate">{d.postUrl}</span>
                    </a>
                  )}
                  {d.lastErrorKind && (
                    <p className="text-xs text-rose-300/70 mt-1">
                      {d.lastErrorKind} (attempts: {d.attemptCount})
                    </p>
                  )}
                  {d.lastError && (
                    <p className="text-xs text-rose-300/50 mt-1 line-clamp-2">{d.lastError}</p>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}
      </section>
    </div>
  );
}
